import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ModalDirective } from 'ng-uikit-pro-standard';
import { ExecutionResultDto } from 'src/app/_models/execution-result-model';
import { ExecutionResult } from 'src/app/_models/execution-result-enum';
import { AiCriteriaUsageDto } from 'src/app/_models/messaging/automation/message-codes/ai-criteria-usage-dto';
import { MessageCodeService } from 'src/app/_services/messaging/automation/message-codes/message-code.service';
import { MessageCodeStepService } from 'src/app/_services/messaging/automation/message-codes/message-code-step.service';
import { NotificationService } from 'src/app/_services/notification.service';
import { LoadingSpinnerService } from 'src/app/_services/loading-spinner/loading-spinner.service';
import { ConfirmDeleteByNameModalComponent } from '../confirm-delete-by-name/confirm-delete-by-name-modal.component';

// 'code' - single AI Criteria code (MessageCodeStep), 'group' - AI Criteria Group (MessageCode)
export interface AiCriteriaDeleteTarget {
  type: 'code' | 'group';
  id: number;
  name: string;
}

@Component({
  selector: 'app-delete-ai-criteria-modal',
  templateUrl: './delete-ai-criteria-modal.component.html',
  styleUrls: ['./delete-ai-criteria-modal.component.scss']
})
export class DeleteAiCriteriaModalComponent {
  @ViewChild('deleteAiCriteriaModal', { static: true }) modal: ModalDirective;
  @ViewChild('confirmDeleteModal', { static: true }) confirmDeleteModal: ConfirmDeleteByNameModalComponent;

  @Output() deleted: EventEmitter<AiCriteriaDeleteTarget> = new EventEmitter<AiCriteriaDeleteTarget>();

  target: AiCriteriaDeleteTarget;
  usages: AiCriteriaUsageDto[] = [];

  constructor(
    private messageCodeService: MessageCodeService,
    private messageCodeStepService: MessageCodeStepService,
    private notificationService: NotificationService,
    private loadingSpinnerService: LoadingSpinnerService
  ) { }

  get isCode(): boolean {
    return this.target?.type === 'code';
  }

  // In-use check is always the first step: the confirm modal is shown only when nothing references the criteria
  open(target: AiCriteriaDeleteTarget): void {
    this.target = target;
    this.usages = [];

    const request: Observable<ExecutionResultDto> = this.isCode
      ? this.messageCodeStepService.getUsages(target.id)
      : this.messageCodeService.getUsages(target.id);

    this.loadingSpinnerService.loading();

    request
      .pipe(finalize(() => this.loadingSpinnerService.stopLoading()))
      .subscribe((executionResultDto: ExecutionResultDto) => {
        if (executionResultDto.executionResult !== ExecutionResult.success) {
          this.notificationService.showError(executionResultDto.message);
          return;
        }

        this.usages = Array.isArray(executionResultDto.data)
          ? executionResultDto.data.map(x => new AiCriteriaUsageDto(x))
          : [];

        if (this.usages.length > 0) {
          this.modal.show();
          return;
        }

        // the API re-checks usages on delete (409 if the criteria was assigned in the meantime)
        this.confirmDeleteModal.open({
          entityName: this.isCode ? 'AI Criteria' : 'AI Criteria Group',
          nameLabel: this.isCode ? 'criteria code' : 'group name',
          matchLabel: this.isCode ? 'Code' : 'Group name',
          name: target.name,
          delete: () => this.isCode
            ? this.messageCodeStepService.delete(target.id)
            : this.messageCodeService.delete(target.id)
        });
      });
  }

  onConfirmedDelete(): void {
    this.deleted.emit(this.target);
  }
}
